import { ArrowLeft, Check, Eye, Save } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../../lib/auth';
import { formatDateTimeLocal } from '../../lib/format';
import { useAsync } from '../../lib/hooks';
import {
  getRepo, ValidationError,
  type Article, type ArticleInput, type ArticleStatus, type Author, type Category,
} from '../../lib/repo';
import { slugify, isValidSlug, MAX_SLUG_LENGTH } from '../../shared/slug.js';
import { canRoleTransition, nextStatuses, roleRank, STATUS_LABEL } from '../../shared/status.js';
import { Badge, Button, Card, CardBody, CardHeader, Field, Input, Select, Spinner, Switch, Textarea, useToast } from '../../components/ui';

const LANGUAGES = [
  { code: 'en', label: 'English' },
  { code: 'te', label: 'Telugu' },
  { code: 'hi', label: 'Hindi' },
];

const REGIONS = [
  { code: '', label: 'No region' },
  { code: 'IN', label: 'India' },
  { code: 'IN-TG', label: 'Telangana' },
  { code: 'IN-AP', label: 'Andhra Pradesh' },
];

interface FormState {
  title: string;
  slug: string;
  dek: string;
  body: string;
  coverImageUrl: string;
  categoryId: string;
  authorId: string;
  languageCode: string;
  regionCode: string;
  tagsText: string;
  status: ArticleStatus;
  scheduledForLocal: string;
  seoTitle: string;
  seoDescription: string;
  seoOgImage: string;
  seoCanonical: string;
  seoNoindex: boolean;
}

const EMPTY: FormState = {
  title: '', slug: '', dek: '', body: '', coverImageUrl: '',
  categoryId: '', authorId: '', languageCode: 'en', regionCode: '', tagsText: '',
  status: 'draft', scheduledForLocal: '',
  seoTitle: '', seoDescription: '', seoOgImage: '', seoCanonical: '', seoNoindex: false,
};

function parseTags(text: string): string[] {
  const out: string[] = [];
  for (const raw of text.split(',')) {
    const trimmed = raw.trim();
    if (!trimmed) continue;
    const s = slugify(trimmed, { fallback: 'tag' });
    if (s && !out.includes(s)) out.push(s);
  }
  return out.slice(0, 20);
}

function fromArticle(a: Article): FormState {
  return {
    title: a.title,
    slug: a.slug,
    dek: a.dek,
    body: a.body,
    coverImageUrl: a.coverImageUrl,
    categoryId: a.categoryId,
    authorId: a.authorId,
    languageCode: a.languageCode || 'en',
    regionCode: a.regionCode || '',
    tagsText: a.tags.join(', '),
    status: a.status,
    scheduledForLocal: formatDateTimeLocal(a.scheduledFor),
    seoTitle: a.seo.title,
    seoDescription: a.seo.description,
    seoOgImage: a.seo.ogImage,
    seoCanonical: a.seo.canonical,
    seoNoindex: a.seo.noindex,
  };
}

export function ArticleEditorPage() {
  const { id } = useParams<{ id?: string }>();
  const navigate = useNavigate();
  const toast = useToast();
  const { roles } = useAuth();
  // The identity the SERVER verified, not one the page assumed.
  const me = useAsync(() => getRepo().getMe(), []);

  const [form, setForm] = useState<FormState>(EMPTY);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [slugTouched, setSlugTouched] = useState(false);
  const [saving, setSaving] = useState(false);
  const [articleId, setArticleId] = useState<string | undefined>(id);

  const categories = useAsync<Category[]>(() => getRepo().listCategories(), []);
  const authors = useAsync<Author[]>(() => getRepo().listAuthors(), []);
  const article = useAsync<Article | null>(() => (id ? getRepo().getArticleById(id) : Promise.resolve(null)), [id]);

  // Seed the form once, from the loaded article.
  useEffect(() => {
    if (id && article.data) {
      setForm(fromArticle(article.data));
      setSlugTouched(true);
      setArticleId(article.data.id);
    }
  }, [id, article.data]);

  const highestRole = useMemo(() => {
    const sorted = [...roles].sort((a, b) => Number(roleRank(b)) - Number(roleRank(a)));
    return sorted[0] ?? 'writer';
  }, [roles]);

  const transitions = useMemo(
    () => (nextStatuses(form.status) as ArticleStatus[]).filter((t) => Boolean(canRoleTransition(highestRole, form.status, t))),
    [form.status, highestRole],
  );

  const slugInvalid = form.slug.length > 0 && !isValidSlug(form.slug);
  const loading = categories.loading || authors.loading || (Boolean(id) && article.loading);

  function set<K extends keyof FormState>(key: K, value: FormState[K]) {
    setForm((f) => ({ ...f, [key]: value }));
  }

  function onTitleChange(value: string) {
    setForm((f) => ({
      ...f,
      title: value,
      // Auto-derive the slug until the editor deliberately edits it.
      ...(slugTouched ? {} : { slug: slugify(value, { fallback: 'article' }) }),
    }));
  }

  function applyTransition(target: ArticleStatus) {
    if (target === 'scheduled' && !form.scheduledForLocal) {
      setErrors((e) => ({ ...e, scheduledFor: 'Choose the date and time to publish.' }));
    }
    set('status', target);
  }

  async function save() {
    const nextErrors: Record<string, string> = {};
    if (form.title.trim().length < 3) nextErrors.title = 'Title is too short';
    if (!form.slug) nextErrors.slug = 'Slug is required';
    else if (!isValidSlug(form.slug)) nextErrors.slug = 'Slug may only contain lowercase letters, numbers and single hyphens';
    if (!form.body.trim()) nextErrors.body = 'Body cannot be empty';
    if (!form.categoryId) nextErrors.categoryId = 'Choose a category';
    if (!form.authorId) nextErrors.authorId = 'Choose an author';
    if (form.seoTitle.length > 70) nextErrors['seo.title'] = 'SEO title must be ≤ 70 characters';
    if (form.seoDescription.length > 160) nextErrors['seo.description'] = 'Meta description must be ≤ 160 characters';
    if (form.status === 'scheduled') {
      if (!form.scheduledForLocal) nextErrors.scheduledFor = 'A scheduled article needs a publish date/time';
      else if (new Date(form.scheduledForLocal).getTime() <= Date.now()) nextErrors.scheduledFor = 'Scheduled time must be in the future';
    }
    if (Object.keys(nextErrors).length) {
      setErrors(nextErrors);
      toast('Fix the highlighted fields before saving', 'error');
      return;
    }

    const input: ArticleInput = {
      id: articleId,
      title: form.title.trim(),
      slug: form.slug.trim(),
      dek: form.dek,
      body: form.body,
      coverImageUrl: form.coverImageUrl,
      categoryId: form.categoryId,
      authorId: form.authorId,
      languageCode: form.languageCode,
      regionCode: form.regionCode,
      tags: parseTags(form.tagsText),
      status: form.status,
      scheduledFor: form.status === 'scheduled' && form.scheduledForLocal
        ? new Date(form.scheduledForLocal).toISOString()
        : null,
      seo: {
        title: form.seoTitle,
        description: form.seoDescription,
        canonical: form.seoCanonical,
        ogImage: form.seoOgImage,
        noindex: form.seoNoindex,
      },
    };

    setSaving(true);
    setErrors({});
    try {
      const saved = await getRepo().saveArticle(input);
      toast(form.status === 'published' ? 'Published' : 'Saved');
      setArticleId(saved.id);
      setForm(fromArticle(saved));
      setSlugTouched(true);
      if (!id) navigate(`/admin/articles/${saved.id}`, { replace: true });
    } catch (e) {
      if (e instanceof ValidationError) {
        setErrors(e.errors);
        toast('Some fields need attention', 'error');
      } else {
        toast(e instanceof Error ? e.message : 'Could not save', 'error');
      }
    } finally {
      setSaving(false);
    }
  }

  if (id && !article.loading && !article.data) {
    return (
      <div className="space-y-4">
        <h1 className="font-display text-2xl font-semibold">Article not found</h1>
        <p className="text-sm text-muted">No article with id “{id}”. It may have been deleted.</p>
        <Link to="/admin/articles"><Button variant="outline"><ArrowLeft className="h-4 w-4" /> Back to articles</Button></Link>
      </div>
    );
  }

  if (loading) return <Spinner label="Loading editor…" />;

  return (
    <div className="space-y-6 pb-20">
      <header className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <Link to="/admin/articles" className="text-xs uppercase tracking-widest text-muted hover:text-primary">← Articles</Link>
          <h1 className="mt-1 font-display text-2xl font-semibold">{id ? 'Edit article' : 'New article'}</h1>
        </div>
        <div className="flex items-center gap-2">
          <Badge tone={form.status}>{STATUS_LABEL[form.status] ?? form.status}</Badge>
          {articleId ? (
            <a href={`/article/${form.slug}`} target="_blank" rel="noreferrer">
              <Button variant="outline" size="sm"><Eye className="h-4 w-4" /> Preview</Button>
            </a>
          ) : null}
        </div>
      </header>

      <div className="grid gap-6 lg:grid-cols-[1fr_340px]">
        <div className="space-y-6">
          <Card>
            <CardHeader title="Story" />
            <CardBody className="space-y-4">
              <Field label="Title" error={errors.title} id="title">
                <Input id="title" value={form.title} onChange={(e) => onTitleChange(e.target.value)} placeholder="Article title" />
              </Field>

              <Field
                label="Slug"
                error={errors.slug ?? (slugInvalid ? 'Slug may only contain lowercase letters, numbers and single hyphens' : undefined)}
                hint={`Final URL: /article/${form.slug || '…'}`}
                id="slug"
              >
                <Input
                  id="slug"
                  value={form.slug}
                  maxLength={MAX_SLUG_LENGTH}
                  onChange={(e) => { setSlugTouched(true); set('slug', e.target.value); }}
                  placeholder="article-slug"
                />
              </Field>

              <div className="grid gap-4 sm:grid-cols-2">
                <Field label="Category" error={errors.categoryId} id="category">
                  <Select id="category" value={form.categoryId} onChange={(e) => set('categoryId', e.target.value)}>
                    <option value="">Select…</option>
                    {(categories.data ?? []).map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                  </Select>
                </Field>
                <Field label="Author" error={errors.authorId} id="author">
                  <Select id="author" value={form.authorId} onChange={(e) => set('authorId', e.target.value)}>
                    <option value="">Select…</option>
                    {(authors.data ?? []).map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
                  </Select>
                </Field>
                <Field label="Language" id="language">
                  <Select id="language" value={form.languageCode} onChange={(e) => set('languageCode', e.target.value)}>
                    {LANGUAGES.map((l) => <option key={l.code} value={l.code}>{l.label}</option>)}
                  </Select>
                </Field>
                <Field label="Region" id="region">
                  <Select id="region" value={form.regionCode} onChange={(e) => set('regionCode', e.target.value)}>
                    {REGIONS.map((r) => <option key={r.code} value={r.code}>{r.label}</option>)}
                  </Select>
                </Field>
              </div>

              <Field label="Tags" hint="Comma-separated; each tag is slugified (e.g. “Assam, temples”)." id="tags">
                <Input id="tags" value={form.tagsText} onChange={(e) => set('tagsText', e.target.value)} placeholder="assam, temples, culture" />
              </Field>

              <Field label="Standfirst / summary" error={errors.dek} hint={`${form.dek.length}/320`} id="dek">
                <Textarea id="dek" rows={2} maxLength={320} value={form.dek} onChange={(e) => set('dek', e.target.value)} placeholder="Brief summary…" />
              </Field>

              <Field label="Cover image URL" error={errors.coverImageUrl} id="cover">
                <Input id="cover" value={form.coverImageUrl} onChange={(e) => set('coverImageUrl', e.target.value)} placeholder="https://…" />
              </Field>
              {form.coverImageUrl ? (
                <img src={form.coverImageUrl} alt="" className="h-40 w-full rounded-lg object-cover" />
              ) : null}

              <Field label="Body" error={errors.body} id="body">
                <Textarea id="body" rows={16} value={form.body} onChange={(e) => set('body', e.target.value)} placeholder="Write the story…" />
              </Field>
            </CardBody>
          </Card>

          <Card>
            <CardHeader
              title="SEO & social"
              description="These values drive the page title, meta description and social preview. Blank SEO title falls back to the headline."
            />
            <CardBody className="space-y-4">
              <Field
                label="SEO title"
                error={errors['seo.title']}
                hint={`${form.seoTitle.length}/70 — shown in search results and social cards`}
                id="seo-title"
              >
                <Input id="seo-title" maxLength={80} value={form.seoTitle} onChange={(e) => set('seoTitle', e.target.value)} placeholder={form.title || 'SEO title'} />
              </Field>
              <Field
                label="Meta description"
                error={errors['seo.description']}
                hint={`${form.seoDescription.length}/160`}
                id="seo-desc"
              >
                <Textarea id="seo-desc" rows={2} maxLength={200} value={form.seoDescription} onChange={(e) => set('seoDescription', e.target.value)} placeholder="One or two sentences…" />
              </Field>
              <div className="grid gap-4 sm:grid-cols-2">
                <Field label="Social image (OG)" error={errors['seo.ogImage']} id="seo-og">
                  <Input id="seo-og" value={form.seoOgImage} onChange={(e) => set('seoOgImage', e.target.value)} placeholder="Defaults to the cover image" />
                </Field>
                <Field label="Canonical URL" error={errors['seo.canonical']} id="seo-canonical">
                  <Input id="seo-canonical" value={form.seoCanonical} onChange={(e) => set('seoCanonical', e.target.value)} placeholder="Defaults to /article/<slug>" />
                </Field>
              </div>
              <div className="flex items-center justify-between rounded-lg border border-border p-3">
                <div>
                  <p className="text-sm font-medium">Hide from search engines</p>
                  <p className="text-xs text-muted">Adds <code>noindex,nofollow</code> to this article.</p>
                </div>
                <Switch checked={form.seoNoindex} onChange={(v) => set('seoNoindex', v)} label="noindex" />
              </div>
            </CardBody>
          </Card>
        </div>

        <div className="space-y-6">
          <Card>
            <CardHeader title="Publishing" description="Only legal transitions for your role are offered." />
            <CardBody className="space-y-4">
              <div className="flex items-center justify-between text-sm">
                <span className="text-muted">Current status</span>
                <Badge tone={form.status}>{STATUS_LABEL[form.status] ?? form.status}</Badge>
              </div>

              <Field
                label="Publish at"
                error={errors.scheduledFor}
                hint="Required when scheduling; must be in the future."
                id="scheduled"
              >
                <Input
                  id="scheduled"
                  type="datetime-local"
                  value={form.scheduledForLocal}
                  onChange={(e) => { set('scheduledForLocal', e.target.value); setErrors((er) => ({ ...er, scheduledFor: '' })); }}
                />
              </Field>

              <div className="space-y-2">
                <p className="text-xs font-medium uppercase tracking-wide text-muted">Move to</p>
                {transitions.length === 0 ? (
                  <p className="text-xs text-muted">No transitions available for your role from this state.</p>
                ) : (
                  transitions.map((t) => (
                    <Button
                      key={t}
                      variant={form.status === t ? 'outline' : t === 'published' ? 'success' : 'outline'}
                      size="sm"
                      className="w-full justify-start"
                      onClick={() => applyTransition(t)}
                    >
                      {form.status === t ? <Check className="h-3.5 w-3.5" /> : null}
                      Move to {STATUS_LABEL[t] ?? t}
                    </Button>
                  ))
                )}
              </div>
            </CardBody>
          </Card>

          <Card>
            <CardHeader title="Checklist" />
            <CardBody className="space-y-2 text-sm">
              {[
                { ok: form.title.trim().length >= 3, label: 'Headline set' },
                { ok: Boolean(form.slug) && !slugInvalid, label: 'URL-safe slug' },
                { ok: Boolean(form.categoryId && form.authorId), label: 'Category & author' },
                { ok: Boolean(form.coverImageUrl), label: 'Cover image' },
                { ok: Boolean(form.seoTitle || form.title), label: 'SEO title (or headline fallback)' },
                { ok: Boolean(form.seoDescription || form.dek), label: 'Meta description (or standfirst fallback)' },
              ].map((row) => (
                <p key={row.label} className={row.ok ? 'text-success' : 'text-muted'}>
                  {row.ok ? '✓' : '○'} {row.label}
                </p>
              ))}
            </CardBody>
          </Card>
        </div>
      </div>

      <div className="sticky bottom-0 z-30 -mx-6 border-t border-border bg-background/95 px-6 py-3 backdrop-blur">
        <div className="mx-auto flex max-w-5xl items-center justify-between gap-3">
          <p className="text-xs text-muted">
            {articleId ? 'Editing an existing article' : 'New article'} · saving as {
              me.data?.name || me.data?.email || 'you'
            }
          </p>
          <div className="flex items-center gap-2">
            <Link to="/admin/articles"><Button variant="ghost" size="sm">Cancel</Button></Link>
            <Button onClick={() => void save()} disabled={saving}>
              <Save className="h-4 w-4" /> {saving ? 'Saving…' : 'Save article'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
